import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import {
  useRoutes,
  useLocation,
  Navigate,
  useSearchParams
} from 'react-router-dom';

import { vKey } from '@accedo/xdk-virtual-key';
import { focusManager } from '@accedo/vdkweb-navigation';
import xdk from '@accedo/xdk-core';

import ErrorPopup from '#/components/ErrorPopup/ErrorPopup';
import NotificationManager from '#/components/NotificationManager/NotificationManager';
import TVHeader from '#/components/Header/TVHeader';
import LearnMoreModal from '#/components/LearnMoreModal/LearnMoreModal';
import PopUpModal from '#/components/PopUpModal/PopUpModal';
import routes from '#/config/routes';
import { ROUTES, APP_QS_KEY } from '#/config/constants';

import useKeyHandler from '#/hooks/useKeyHandler';
import useHistoryPush from '#/hooks/history/useHistoryPush';
import useHistoryBack from '#/hooks/history/useHistoryBack';
import useGetHistoryStack from '#/hooks/history/useGetHistoryStack';
import useResetStack from '#/hooks/history/useResetStack';
import useAuthProfile from '#/hooks/useAuthProfile';
import useAppConfig from '#/hooks/useAppConfig';
import useEcoModal from '#/hooks/useEcoModal';
import useEcoProfile from '#/hooks/useEcoProfile';

import styles from './app.scss';

const LANDING_PATHS = ['/']; // TODO: dev review this with your CMS config

const shouldRedirectToProfileSelection = !__PROFILE_SELECTION_INITIAL_DISABLED__;

let firstLoad = true;

const isCurrentPathHome = pathname => {
  return LANDING_PATHS.includes(pathname);
};

const showEcoTip = ({
  pathname,
  showPopUpOnClick,
  getRandomEcoTip,
  timeoutDelay,
  rotationTimeoutDelay,
  idleTimeoutRef,
  previousNavId,
  navIdToRestoreFocusToRef
}) => {
  const allowedRoutes = [
    ROUTES.movieDetails,
    ROUTES.movies,
    ROUTES.show,
    ROUTES.shows,
    ROUTES.viewAll,
    ROUTES.category
  ];

  return setTimeout(() => {
    const isHome = pathname === ROUTES.home;
    const currentNavId = previousNavId || focusManager.getCurrentFocus();

    if (allowedRoutes.some(route => pathname.includes(route)) || isHome) {
      showPopUpOnClick(currentNavId, getRandomEcoTip());

      // This Keeps switching tip data every timeoutDelay milliseconds
      navIdToRestoreFocusToRef.current = currentNavId;
      idleTimeoutRef.current.id = showEcoTip({
        pathname,
        showPopUpOnClick,
        getRandomEcoTip,
        timeoutDelay: rotationTimeoutDelay,
        rotationTimeoutDelay,
        idleTimeoutRef,
        navIdToRestoreFocusToRef,
        previousNavId: currentNavId
      });
    }
  }, timeoutDelay);
};

const PAGES_THAT_OVERWRITES_BACK_EVENT = [
  ROUTES.profileSelection,
  ROUTES.manageProfile,
  ROUTES.createProfile
];

const App = () => {
  const historyPush = useHistoryPush();
  const historyBack = useHistoryBack();
  const {
    ecoProfileOptionsModalOpenRef,
    smallEcoProfileOptionsModalOpenRef
  } = useEcoProfile();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const getHistoryStack = useGetHistoryStack();
  const resetStack = useResetStack();
  const { config = {} } = useAppConfig();
  const { quickOnboarding } = useEcoProfile();
  const { isAuthenticated } = useAuthProfile();
  const navIdToRestoreFocusToRef = useRef(null);
  const {
    showPopUpOnClick,
    hidePopUpOnClick,
    getEcoTips,
    getRandomEcoTip,
    getEcoTipsIdleTimeoutDelay,
    isPopUpModalVisible,
    isLearnMoreModalVisible,
    hideModalOnClick,
    getEcoTipRotationTimeoutDelay
  } = useEcoModal({ navIdToRestoreFocusToRef });
  if (searchParams.size > 0) {
    xdk.storage.set(APP_QS_KEY, searchParams);
  }
  const {
    application: applicationConfiguration = {},
    routes: routesFromConfig
  } = config;
  const { navigation: { defaultRoute } = {} } = applicationConfiguration;
  const routesRef = useRef([]);
  const idleTimeoutRef = useRef({ id: null });

  const exitAction = useCallback(() => {
    try {
      xdk.device.system.exit();
    } catch (e) {
      console.warn(e);
    }
  }, []);

  const isHome = useMemo(() => isCurrentPathHome(pathname), [pathname]);

  const shouldExitApplication = useCallback(() => {
    return isHome;
  }, [isHome]);

  const doesThePageOverwriteTheBackEvent = useCallback(() => {
    return !!PAGES_THAT_OVERWRITES_BACK_EVENT.find(page =>
      pathname.includes(page)
    );
  }, [pathname]);

  useEffect(() => {
    navIdToRestoreFocusToRef.current = null;
    clearTimeout(idleTimeoutRef.current.id);

    const handleRouteInitialFocusChange = data => {
      if (
        pathname === '/' &&
        data &&
        data.currentFocus &&
        (!data.currentFocus.includes('grid-item') || getEcoTips().length === 0)
      ) {
        // This means we'll wait until the first item besides the menu gets focused so we can begin the inactivity timeouts
        return;
      }

      clearTimeout(idleTimeoutRef.current.id);
      focusManager.unlistenToFocusChanged(handleRouteInitialFocusChange);
      idleTimeoutRef.current.id = showEcoTip({
        pathname,
        showPopUpOnClick,
        getRandomEcoTip,
        timeoutDelay: getEcoTipsIdleTimeoutDelay(),
        rotationTimeoutDelay: getEcoTipRotationTimeoutDelay(),
        idleTimeoutRef,
        navIdToRestoreFocusToRef
      });
    };

    focusManager.listenToFocusChanged(handleRouteInitialFocusChange);

    return () => {
      focusManager.unlistenToFocusChanged(handleRouteInitialFocusChange);
    };
  }, [pathname]);

  const keyHandler = useCallback(
    ({ id }, e) => {
      const { UP, DOWN, LEFT, RIGHT, OK, BACK, EXIT } = vKey;
      const KEY_EVENT_PREFIX = 'device:vkey:';
      const isHistoryEmpty = !getHistoryStack().length;
      const currentFocus = focusManager.getCurrentFocus();
      const isArrowButton = currentFocus?.includes?.('arrow');
      let containerId;
      // if the current component handles the pressed key
      // the deafult handler does nothing

      clearTimeout(idleTimeoutRef.current.id);
      idleTimeoutRef.current.id = showEcoTip({
        pathname,
        showPopUpOnClick,
        getRandomEcoTip,
        timeoutDelay: getEcoTipsIdleTimeoutDelay(),
        rotationTimeoutDelay: getEcoTipRotationTimeoutDelay(),
        idleTimeoutRef,
        navIdToRestoreFocusToRef,
        previousNavId: navIdToRestoreFocusToRef.current
      });

      switch (id) {
        case UP.id:
        case DOWN.id:
        case LEFT.id:
        case RIGHT.id: {
          // Carousel Arrow will dissapear when using navigation buttons, we need to reset focus
          if (isArrowButton) {
            [containerId] = currentFocus.split('-');
            focusManager.changeFocus(containerId);
            e.stopImmediatePropagation();
            return;
          }
          // Normal behaviour
          const direction = `next${id.substring(KEY_EVENT_PREFIX.length)}`;
          focusManager.navigateFocus(direction);
          break;
        }
        case OK.id:
          focusManager.click();
          break;
        case BACK.id:
          if (isPopUpModalVisible() && !pathname.includes(ROUTES.player)) {
            hidePopUpOnClick(false);
          } else if (isLearnMoreModalVisible()) {
            hideModalOnClick();
          } else if (doesThePageOverwriteTheBackEvent()) {
            break;
          } else if (isHistoryEmpty) {
            if (shouldExitApplication()) {
              exitAction();
            } else {
              historyPush({ path: ROUTES.home });
              resetStack();
            }
          } else if (
            !ecoProfileOptionsModalOpenRef.current &&
            !smallEcoProfileOptionsModalOpenRef.current
          ) {
            historyBack();
          }
          break;
        case EXIT.id:
          exitAction();
          break;
        default:
          break;
      }
    },
    [
      getHistoryStack,
      doesThePageOverwriteTheBackEvent,
      exitAction,
      shouldExitApplication,
      historyPush,
      resetStack,
      historyBack
    ]
  );
  useKeyHandler(keyHandler);

  useEffect(() => {
    const appBody = document.getElementsByTagName('body')[0];
    appBody.style.removeProperty('background-color');
  }, []);

  if (routesRef.current.length <= 0) {
    routesRef.current = routes(routesFromConfig);
  }
  const routes_ = useRoutes(routesRef.current);

  if (defaultRoute && firstLoad && isHome) {
    const query = new URLSearchParams(window.location.search);
    // IMPORTANT: in local development search is empty because of the hash
    const hrefWithModeDev = window.location.href.indexOf('?mode=dev') > 0;
    const isDevBuildForMeasure = query.get('mode') === 'dev' || hrefWithModeDev;

    if (isDevBuildForMeasure) {
      console.log('MODE_DEV IS ACTIVE ', JSON.stringify(window.location));
      quickOnboarding();
    }

    firstLoad = false;
    if (isAuthenticated && shouldRedirectToProfileSelection) {
      return (
        <Navigate
          to={ROUTES.profileSelection}
          state={{ redirection: defaultRoute }}
        />
      );
    }
    return <Navigate to={defaultRoute} />;
  }

  return (
    <div className={styles.viewport}>
      <div className={styles.wrapper}>
        <ErrorPopup />
        <NotificationManager />
        <TVHeader />
        <LearnMoreModal />
        <PopUpModal />
        <div className={styles.content}>{routes_}</div>
      </div>
    </div>
  );
};

export default App;
